import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { authFetch } from "../utils/authFetch";
import { useAuth } from "../contexts/AuthContext";

export interface BootstrapData {
  user: any;
  sections: any[];
  tree: any[];
  page: any | null;
  buildNumber: number;
  contentHash: string;
}

interface UseDocsBootstrapResult {
  data: BootstrapData | null;
  loading: boolean;
  error: Error | null;
}

/**
 * Fires a single GET /api/docs/{projectSlug}/bootstrap call on initial mount.
 * Hydrates AuthContext with the user profile, and returns sections/tree/page.
 * Does NOT re-fire on navigation (slugPath changes) — only on mount.
 */
export function useDocsBootstrap(enabled: boolean = true): UseDocsBootstrapResult {
  const { projectSlug, sectionSlug, "*": remainingPath } = useParams<{
    projectSlug: string;
    sectionSlug: string;
    "*": string;
  }>();
  const { hydrateUser } = useAuth();

  const [data, setData] = useState<BootstrapData | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<Error | null>(null);

  // Capture initial URL params at mount time so navigation doesn't re-trigger
  const initialParams = useRef({ projectSlug, sectionSlug, remainingPath });

  useEffect(() => {
    if (!enabled || !initialParams.current.projectSlug) return;

    let cancelled = false;
    const { projectSlug: pSlug, sectionSlug: sSlug, remainingPath: rPath } = initialParams.current;

    (async () => {
      try {
        let pagePath: string | undefined;
        if (sSlug) {
          pagePath = rPath ? `${sSlug}/${rPath}` : sSlug;
        }

        const url = pagePath
          ? `/api/docs/${pSlug}/bootstrap?page_path=${encodeURIComponent(pagePath)}`
          : `/api/docs/${pSlug}/bootstrap`;

        const res = await authFetch(url);
        if (cancelled) return;

        if (res.status === 401) {
          window.location.href = "/login";
          return;
        }

        if (!res.ok) {
          throw new Error(`Bootstrap failed: ${res.status}`);
        }

        const json = await res.json();
        if (cancelled) return;

        hydrateUser(json.user);
        authFetch("/api/media/cookies", { credentials: "include" }).catch(() => {});

        setData({
          user: json.user,
          sections: json.sections,
          tree: json.tree,
          page: json.page,
          buildNumber: json.buildNumber,
          contentHash: json.contentHash,
        });
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error(String(err)));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => { cancelled = true; };
  // Only fire on mount — not on URL changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  return { data, loading, error };
}
